import React, { useState, useEffect } from 'react';
import { policyRuleAPI, planAPI, userTypeAPI, membershipTierAPI, branchAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './PolicyRules.css';

const ROOM_TYPES = ['standard', 'deluxe', 'suite', 'presidential', 'executive', 'villa', 'other'];
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DEFAULT_FORM = {
    name: '', branchId: '', priority: 0, isActive: true,
    conditions: { userTypeIds: [], vlanIds: '', roomTypes: [], membershipTierIds: [], minStayDays: '', daysOfWeek: [], timeStart: '', timeEnd: '' },
    action: { planId: '', bandwidth: { uploadSpeed: '', downloadSpeed: '' } },
};

const PolicyRules = () => {
    const { selectedBranch } = useBranchStore();
    const [rules, setRules] = useState([]);
    const [plans, setPlans] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => { fetchAll(); }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [ruleRes, planRes, utRes, tierRes, branchRes] = await Promise.all([
                policyRuleAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId), userTypeAPI.getAll(branchId ? { branchId } : undefined), membershipTierAPI.getAll(branchId ? { branchId } : undefined), branchAPI.getAll(),
            ]);
            setRules(ruleRes.data.data || []);
            setPlans(planRes.data.data.plans || []);
            setUserTypes(utRes.data.data || []);
            setTiers(tierRes.data.data || []);
            setBranches(branchRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch policy rules');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => { setEditing(null); setFormData({ ...DEFAULT_FORM, branchId: selectedBranch?._id || '' }); setShowModal(true); };

    const handleEdit = (rule) => {
        setEditing(rule);
        setFormData({
            name: rule.name, branchId: rule.branchId?._id || rule.branchId || '', priority: rule.priority, isActive: rule.isActive !== false,
            conditions: {
                userTypeIds: rule.conditions?.userTypeIds || [],
                vlanIds: (rule.conditions?.vlanIds || []).join(', '),
                roomTypes: rule.conditions?.roomTypes || [],
                membershipTierIds: rule.conditions?.membershipTierIds || [],
                minStayDays: rule.conditions?.minStayDays || '',
                daysOfWeek: rule.conditions?.daysOfWeek || [],
                timeStart: rule.conditions?.timeStart || '',
                timeEnd: rule.conditions?.timeEnd || '',
            },
            action: {
                planId: rule.action?.planId?._id || rule.action?.planId || '',
                bandwidth: { uploadSpeed: rule.action?.bandwidth?.uploadSpeed || '', downloadSpeed: rule.action?.bandwidth?.downloadSpeed || '' },
            },
        });
        setShowModal(true);
    };

    const toggleArrayValue = (key, value) => {
        setFormData(prev => {
            const arr = prev.conditions[key];
            const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
            return { ...prev, conditions: { ...prev.conditions, [key]: next } };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return toast.warning('Name is required');
        const payload = {
            ...formData,
            conditions: {
                ...formData.conditions,
                vlanIds: formData.conditions.vlanIds
                    ? formData.conditions.vlanIds.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
                    : [],
                minStayDays: formData.conditions.minStayDays ? Number(formData.conditions.minStayDays) : undefined,
            },
        };
        try {
            if (editing) {
                await policyRuleAPI.update(editing._id, payload);
                toast.success('Policy rule updated');
            } else {
                await policyRuleAPI.create(payload);
                toast.success('Policy rule created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (rule) => {
        if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
        try {
            await policyRuleAPI.delete(rule._id);
            toast.success('Rule deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-sitemap"></i> Policy Engine</h1>
                        <p className="page-subtitle-modern">Combine user type, VLAN, room category, membership tier, time/day, and stay duration into one rule — highest priority match wins</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}><i className="fas fa-plus-circle"></i> Add Rule</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading rules...</p></div>
            ) : rules.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-sitemap"></i></div>
                    <h3>No Policy Rules Yet</h3>
                    <p>Optional — VLAN profiles, User Types, and Plan time windows already work standalone. Use this only when you need to combine multiple conditions in one rule.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {rules.map(rule => (
                        <div key={rule._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: '#6366f115', color: '#6366f1' }}><i className="fas fa-sitemap"></i></div>
                                <div className="resource-title">
                                    <h3>{rule.name}</h3>
                                    <span className="resource-subtitle">Priority {rule.priority} · {rule.branchId?.name || 'All Branches'}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon edit" onClick={() => handleEdit(rule)}><i className="fas fa-edit"></i></button>
                                    <button className="action-btn-icon delete" onClick={() => handleDelete(rule)}><i className="fas fa-trash"></i></button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${rule.isActive ? 'active' : 'inactive'}`}><span className="badge-dot"></span>{rule.isActive ? 'Active' : 'Inactive'}</span>
                                    {rule.action?.planId && <span className="status-badge inactive">{rule.action.planId.name}</span>}
                                </div>
                                <div className="info-grid">
                                    <div className="info-item"><i className="fas fa-network-wired"></i><div><span className="info-label">VLANs</span><span className="info-value">{rule.conditions?.vlanIds?.join(', ') || 'Any'}</span></div></div>
                                    <div className="info-item"><i className="fas fa-clock"></i><div><span className="info-label">Time Window</span><span className="info-value">{rule.conditions?.timeStart ? `${rule.conditions.timeStart}-${rule.conditions.timeEnd}` : 'Any'}</span></div></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit Policy Rule' : 'Add Policy Rule'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Priority (higher wins)</label>
                                        <input type="number" value={formData.priority} onChange={e => setFormData({ ...formData, priority: Number(e.target.value) })} />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Branch</label>
                                        <select value={formData.branchId} onChange={e => setFormData({ ...formData, branchId: e.target.value })}>
                                            <option value="">All Branches (tenant-wide)</option>
                                            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="section-title"><i className="fas fa-filter"></i> Conditions (all must match — leave empty for "any")</h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>VLAN IDs (comma-separated)</label>
                                            <input type="text" value={formData.conditions.vlanIds} onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, vlanIds: e.target.value } })} placeholder="e.g. 10, 20" />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Min Stay (days)</label>
                                            <input type="number" value={formData.conditions.minStayDays} onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, minStayDays: e.target.value } })} />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Time Start</label>
                                            <input type="time" value={formData.conditions.timeStart} onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, timeStart: e.target.value } })} />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Time End</label>
                                            <input type="time" value={formData.conditions.timeEnd} onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, timeEnd: e.target.value } })} />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>User Types</label>
                                            <div className="chip-row">
                                                {userTypes.map(ut => (
                                                    <label key={ut._id} className={`chip ${formData.conditions.userTypeIds.includes(ut._id) ? 'chip-active' : ''}`}>
                                                        <input type="checkbox" checked={formData.conditions.userTypeIds.includes(ut._id)} onChange={() => toggleArrayValue('userTypeIds', ut._id)} />
                                                        {ut.name}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>Membership Tiers</label>
                                            <div className="chip-row">
                                                {tiers.map(t => (
                                                    <label key={t._id} className={`chip ${formData.conditions.membershipTierIds.includes(t._id) ? 'chip-active' : ''}`}>
                                                        <input type="checkbox" checked={formData.conditions.membershipTierIds.includes(t._id)} onChange={() => toggleArrayValue('membershipTierIds', t._id)} />
                                                        {t.name}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>Room Types</label>
                                            <div className="chip-row">
                                                {ROOM_TYPES.map(rt => (
                                                    <label key={rt} className={`chip ${formData.conditions.roomTypes.includes(rt) ? 'chip-active' : ''}`}>
                                                        <input type="checkbox" checked={formData.conditions.roomTypes.includes(rt)} onChange={() => toggleArrayValue('roomTypes', rt)} />
                                                        {rt}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>Days of Week</label>
                                            <div className="chip-row">
                                                {DAYS.map(d => (
                                                    <label key={d} className={`chip ${formData.conditions.daysOfWeek.includes(d) ? 'chip-active' : ''}`}>
                                                        <input type="checkbox" checked={formData.conditions.daysOfWeek.includes(d)} onChange={() => toggleArrayValue('daysOfWeek', d)} />
                                                        {d.slice(0, 3)}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="section-title"><i className="fas fa-bolt"></i> Action</h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern full-width">
                                            <label>Assign Plan</label>
                                            <select value={formData.action.planId} onChange={e => setFormData({ ...formData, action: { ...formData.action, planId: e.target.value } })}>
                                                <option value="">None</option>
                                                {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Bandwidth Override — Download (Kbps)</label>
                                            <input type="number" value={formData.action.bandwidth.downloadSpeed} onChange={e => setFormData({ ...formData, action: { ...formData.action, bandwidth: { ...formData.action.bandwidth, downloadSpeed: e.target.value } } })} />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Bandwidth Override — Upload (Kbps)</label>
                                            <input type="number" value={formData.action.bandwidth.uploadSpeed} onChange={e => setFormData({ ...formData, action: { ...formData.action, bandwidth: { ...formData.action.bandwidth, uploadSpeed: e.target.value } } })} />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                                <span>Active</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PolicyRules;
